import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import isEqual from 'lodash/isEqual'

import { ActivityV2 } from 'models/activityV2'
import { Skeleton } from 'components/FbUI/Skeleton'
import { FbModal } from 'components/UI/Modal'
import {
  ActionsDropdown,
  createDeleteAction,
  createEditAction,
} from 'components/Buttons/ActionsDropdown'
import dialogConfirm from 'components/dialogConfirm'
import { useAuth } from 'context/authentication/useAuth'
import {
  useDeleteCapture,
  useGetCapture,
  useGetCaptureForm,
  useUpdateCapture,
} from 'services/captureForms'
import { CaptureFormRenderer } from 'components/CaptureForms/CaptureFormRenderer'
import type { CaptureSubmitValues } from 'components/CaptureForms/CaptureFormRenderer'
import type { CaptureValue } from 'models/captureForms'
import { BaseEventCard } from './BaseEventCard'

interface CaptureEventCardProps {
  event: ActivityV2
  /** Refresh the timeline after an edit/delete (owned by EventHistory). */
  onChanged?: () => void
}

/** Render a set of answered values as label: value rows (header or one line). */
function CaptureValueRows({ values }: { values: CaptureValue[] }) {
  return (
    <dl className="space-y-0.5 text-sm text-fg-secondary">
      {values.map((v) => (
        <div key={v.field_key} className="flex gap-1">
          <dt className="font-medium text-fg shrink-0">{v.field_label}:</dt>
          <dd className="truncate">
            {v.display_value ??
              (v.value !== null &&
              v.value !== undefined &&
              typeof v.value !== 'object'
                ? String(v.value)
                : '—')}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * Timeline card for capture submissions (win/loss and change-in-demand).
 * Fetches Capture detail by the object_id in field_history_objects[0], renders
 * the form label and submitted values, and — when permitted — offers Edit
 * (forms with allow_edits, via the same renderer prefilled) and admin Delete.
 */
export function CaptureEventCard({ event, onChanged }: CaptureEventCardProps) {
  const captureId = event.field_history_objects?.[0]?.object_id ?? 0
  const { data: record, isLoading } = useGetCapture(captureId)
  const { isAdmin } = useAuth()

  const [editing, setEditing] = useState(false)
  const { data: form } = useGetCaptureForm(record?.capture_form ?? 0)
  const updateCapture = useUpdateCapture()
  const deleteCapture = useDeleteCapture()
  const queryClient = useQueryClient()

  // Refresh the timeline (dealActivity/companyActivity infinite queries) after
  // an edit/delete so a removed/changed capture is reflected without a reload.
  function refreshTimeline() {
    queryClient.invalidateQueries({
      predicate: (q) =>
        q.queryKey[0] === 'dealActivity' || q.queryKey[0] === 'companyActivity',
    })
    onChanged?.()
  }

  const canEdit = !!record?.allow_edits
  const canDelete = isAdmin

  const initialValues = record
    ? Object.fromEntries(record.values.map((v) => [v.field_key, v.value]))
    : {}

  async function handleDelete() {
    if (!record) return
    if (
      await dialogConfirm(
        'Delete this capture? This removes it from the timeline and reporting.',
        'Delete capture'
      )
    ) {
      await deleteCapture.mutateAsync(record.id).catch(() => {})
      refreshTimeline()
    }
  }

  async function handleEditSubmit({ values }: CaptureSubmitValues) {
    // Line rows are not edited in place (v1); only header values round-trip here.
    if (!record) return
    // Send only the fields whose value actually changed. UpdateCaptureUseCase
    // validates just the keys it receives (partial-edit contract), so submitting
    // the full form state would re-resolve untouched fields — and 400 on an
    // untouched product_variant whose variant was later soft-deleted.
    const changed = Object.fromEntries(
      Object.entries(values).filter(
        ([key, v]) => !isEqual(v, initialValues[key])
      )
    )
    const [err] = await updateCapture
      .mutateAsync({ id: record.id, values: changed })
      .then((d) => [null, d] as const)
      .catch((e) => [e, null] as const)
    if (!err) {
      setEditing(false)
      refreshTimeline()
    }
  }

  const actions = [
    ...(canEdit ? [createEditAction(() => setEditing(true))] : []),
    ...(canDelete ? [createDeleteAction(handleDelete)] : []),
  ]

  return (
    <BaseEventCard event={event}>
      <div className="space-y-1">
        <div className="flex items-start justify-between gap-2">
          {/* Header: "<user_name> logged "<context_label>"" */}
          <div className="font-inter text-base leading-[1.43] text-fg-secondary">
            <span className="text-fg font-medium">{event.user_name}</span>{' '}
            logged{' '}
            {isLoading ? (
              <Skeleton className="inline-block h-4 w-32 align-middle" />
            ) : (
              <span className="text-fg font-medium">
                &ldquo;{record?.form_label ?? 'capture'}&rdquo;
              </span>
            )}
          </div>
          {record && actions.length > 0 && (
            <ActionsDropdown actions={actions} />
          )}
        </div>

        {/* Captured field values */}
        {isLoading ? (
          <div className="space-y-1 mt-1">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        ) : (
          record && (
            <div className="mt-1 space-y-1">
              {record.values.length > 0 && (
                <CaptureValueRows values={record.values} />
              )}
              {/* Line-item rows (e.g. product rows on a call report). */}
              {record.lines?.map((line, idx) => (
                <div
                  key={line.id}
                  className="rounded border border-line px-2 py-1"
                >
                  <div className="text-xs font-semibold text-fg-secondary">
                    Row {idx + 1}
                  </div>
                  <CaptureValueRows values={line.values} />
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {editing && form && (
        <FbModal
          open={editing}
          title={`Edit ${record?.form_label ?? 'capture'}`}
          onOpenChange={setEditing}
        >
          <CaptureFormRenderer
            form={form}
            initialValues={initialValues}
            onSubmit={handleEditSubmit}
            submitting={updateCapture.isPending}
          />
        </FbModal>
      )}
    </BaseEventCard>
  )
}
